import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import {
  BedDouble, Users, LogOut as LogOutIcon, Clock, Percent, RefreshCw as RefreshIcon,
  Wallet, CreditCard, ShieldCheck, AlertTriangle, Package, TrendingUp, Stethoscope,
  UserRound, FileDown, Mail, FileSpreadsheet, Filter,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  View-model                                                                */
/*                                                                            */
/*  Backend `GET /api/ipd/analytics/summary` returns snake_case; we map it    */
/*  once to the camelCase shape the recharts sections expect. This keeps      */
/*  the section code identical to the mock skeleton it was scaffolded from.   */
/* -------------------------------------------------------------------------- */

const EMPTY_DATA = {
  keyMetrics: {
    occupied_beds: 0, total_beds: 0,
    admissions_today: 0, discharged_today: 0,
    avg_los_days: 0, occupancy_pct: 0, bed_turnover_rate: 0,
  },
  revenueLast30d: [],
  revenueByWard: [],
  paymentMix: [],
  patientStatusMix: [],
  losBuckets: [],
  topDoctors: [],
  ratios: { discharge_rate_pct: 0, readmission_rate_pct: 0 },
  wardTable: [],
  billing: {
    pending_bills: 0, overdue_bills: 0,
    collection_rate_pct: 0, avg_bill_inr: 0,
    insurance_pending: 0, insurance_approved: 0, insurance_rejected: 0,
  },
  inventory: { low_stock: [], expiring_soon: [], top_consumed: [] },
  deptCompare: [],
  staff: { doctor_utilization: [], nurse_ratio: "—" },
};

function toViewModel(payload) {
  if (!payload) return EMPTY_DATA;
  return {
    keyMetrics: payload.key_metrics || EMPTY_DATA.keyMetrics,
    revenueLast30d: payload.revenue_last_30d || [],
    revenueByWard: payload.revenue_by_ward || [],
    paymentMix: payload.payment_mix || [],
    patientStatusMix: payload.patient_status_mix || [],
    losBuckets: payload.los_buckets || [],
    topDoctors: payload.top_doctors || [],
    ratios: payload.ratios || EMPTY_DATA.ratios,
    wardTable: payload.ward_table || [],
    billing: payload.billing || EMPTY_DATA.billing,
    inventory: payload.inventory || EMPTY_DATA.inventory,
    deptCompare: payload.dept_compare || [],
    staff: payload.staff || EMPTY_DATA.staff,
  };
}


const PIE_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

/* -------------------------------------------------------------------------- */
/*  Small primitives                                                          */
/* -------------------------------------------------------------------------- */

function MetricCard({ icon: Icon, label, value, hint, tone = "sky", testid }) {
  const toneMap = {
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    violet: "bg-violet-50 text-violet-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">{label}</p>
        <div className={`p-1.5 rounded-md ${toneMap[tone] || toneMap.sky}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-heading text-3xl font-bold mt-3 text-slate-900">{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </Card>
  );
}

function SectionHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
      <div>
        <h3 className="font-heading text-xl font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Filters + Export bar                                                      */
/* -------------------------------------------------------------------------- */

function FiltersBar({ filters, setFilters, doctors, wards }) {
  const doExport = (fmt) => toast.success(`Export queued (${fmt.toUpperCase()}) — mock`);
  return (
    <Card className="p-4" data-testid="ipd-filters-bar">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="w-4 h-4 text-slate-500" />
        <p className="text-sm font-medium text-slate-700">Filters &amp; Export</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={filters.from}
                 onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                 data-testid="ipd-filter-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={filters.to}
                 onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                 data-testid="ipd-filter-to" />
        </div>
        <div>
          <Label className="text-xs">Ward</Label>
          <Select value={filters.ward} onValueChange={(v) => setFilters({ ...filters, ward: v })}>
            <SelectTrigger data-testid="ipd-filter-ward"><SelectValue placeholder="All wards" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All wards</SelectItem>
              {wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Doctor</Label>
          <Select value={filters.doctor} onValueChange={(v) => setFilters({ ...filters, doctor: v })}>
            <SelectTrigger data-testid="ipd-filter-doctor"><SelectValue placeholder="All doctors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All doctors</SelectItem>
              {doctors.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Payment</Label>
          <Select value={filters.payment} onValueChange={(v) => setFilters({ ...filters, payment: v })}>
            <SelectTrigger data-testid="ipd-filter-payment"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="insurance">Insurance</SelectItem>
              <SelectItem value="gov">Gov Scheme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-1">
          <Button variant="outline" size="sm" onClick={() => doExport("pdf")}
                  className="flex-1" data-testid="ipd-export-pdf">
            <FileDown className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => doExport("csv")}
                  className="flex-1" data-testid="ipd-export-csv">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => doExport("email")}
                  className="flex-1" data-testid="ipd-export-email">
            <Mail className="w-4 h-4 mr-1" /> Email
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 1 · Key Metrics                                                   */
/* -------------------------------------------------------------------------- */

function KeyMetrics({ data }) {
  const m = data.keyMetrics;
  return (
    <section data-testid="ipd-key-metrics">
      <SectionHeader title="Key Metrics" subtitle="Live snapshot of the IPD floor" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard testid="ipd-metric-occupied" icon={BedDouble} label="Occupied Beds"
                    value={`${m.occupied_beds} / ${m.total_beds}`} tone="sky" />
        <MetricCard testid="ipd-metric-admissions" icon={Users} label="Admissions Today"
                    value={m.admissions_today} tone="emerald" />
        <MetricCard testid="ipd-metric-discharged" icon={LogOutIcon} label="Discharged Today"
                    value={m.discharged_today} tone="violet" />
        <MetricCard testid="ipd-metric-los" icon={Clock} label="Avg LOS (days)"
                    value={m.avg_los_days} tone="amber" />
        <MetricCard testid="ipd-metric-occupancy" icon={Percent} label="Occupancy %"
                    value={`${m.occupancy_pct}%`} tone="rose" />
        <MetricCard testid="ipd-metric-turnover" icon={RefreshIcon} label="Bed Turnover"
                    value={m.bed_turnover_rate} hint="admissions / bed / month" tone="slate" />
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 2 · Revenue Dashboard                                             */
/* -------------------------------------------------------------------------- */

function RevenueDashboard({ data }) {
  return (
    <section data-testid="ipd-revenue-dashboard">
      <SectionHeader title="Revenue Dashboard" subtitle="Trend, ward split, and payment mix" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2" data-testid="ipd-chart-revenue-trend">
          <p className="text-sm font-medium text-slate-700 mb-2">Revenue — last 30 days</p>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={data.revenueLast30d}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="d" fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" tickFormatter={(v) => `₹${v/1000}k`} />
                <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4" data-testid="ipd-chart-payment-mix">
          <p className="text-sm font-medium text-slate-700 mb-2">Payment method mix</p>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.paymentMix} dataKey="value" nameKey="name" outerRadius={80} label>
                  {data.paymentMix.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 lg:col-span-3" data-testid="ipd-chart-revenue-by-ward">
          <p className="text-sm font-medium text-slate-700 mb-2">Revenue by ward</p>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.revenueByWard}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="ward" fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" tickFormatter={(v) => `₹${v/1000}k`} />
                <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Bar dataKey="revenue" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 3 · Patient Analytics                                             */
/* -------------------------------------------------------------------------- */

function PatientAnalytics({ data }) {
  return (
    <section data-testid="ipd-patient-analytics">
      <SectionHeader title="Patient Analytics" subtitle="Length of stay, top prescribers, outcomes" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="ipd-chart-active-vs-discharged">
          <p className="text-sm font-medium text-slate-700 mb-2">Active vs Discharged (30d)</p>
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.patientStatusMix} dataKey="value" nameKey="name"
                     innerRadius={40} outerRadius={70} label>
                  <Cell fill="#0ea5e9" />
                  <Cell fill="#94a3b8" />
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 md:col-span-2" data-testid="ipd-chart-los-histogram">
          <p className="text-sm font-medium text-slate-700 mb-2">Length of stay (patients)</p>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={data.losBuckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="bucket" fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 space-y-3" data-testid="ipd-outcome-ratios">
          <p className="text-sm font-medium text-slate-700">Outcome ratios</p>
          <div>
            <p className="text-xs uppercase text-slate-500">Discharge rate</p>
            <p className="font-heading text-2xl font-bold text-emerald-600">{data.ratios.discharge_rate_pct}%</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Readmission rate (30d)</p>
            <p className="font-heading text-2xl font-bold text-rose-600">{data.ratios.readmission_rate_pct}%</p>
          </div>
        </Card>

        <Card className="p-4 lg:col-span-4" data-testid="ipd-top-doctors">
          <p className="text-sm font-medium text-slate-700 mb-3">Top doctors by IPD admissions</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Doctor</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Admissions</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.topDoctors.map((d) => (
                <TableRow key={d.name}>
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell className="text-slate-600">{d.dept}</TableCell>
                  <TableCell className="text-right">{d.admissions}</TableCell>
                  <TableCell className="text-right text-emerald-700 font-semibold">
                    ₹{d.revenue.toLocaleString("en-IN")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 4 · Ward Management Table                                         */
/* -------------------------------------------------------------------------- */

function WardManagement({ data }) {
  return (
    <section data-testid="ipd-ward-management">
      <SectionHeader title="Ward Management" subtitle="Occupancy, rates, turnover and revenue by ward" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Ward</TableHead>
              <TableHead className="text-right">Total beds</TableHead>
              <TableHead className="text-right">Occupied</TableHead>
              <TableHead className="text-right">Occupancy</TableHead>
              <TableHead className="text-right">Avg rate</TableHead>
              <TableHead className="text-right">Bed turnover</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.wardTable.map((w) => (
              <TableRow key={w.name} data-testid={`ipd-ward-row-${w.name.toLowerCase()}`}>
                <TableCell className="font-medium">{w.name}</TableCell>
                <TableCell className="text-right">{w.total}</TableCell>
                <TableCell className="text-right">{w.occ}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={w.occ_pct >= 90 ? "destructive" : w.occ_pct >= 70 ? "default" : "outline"}>
                    {w.occ_pct}%
                  </Badge>
                </TableCell>
                <TableCell className="text-right">₹{w.avg_rate.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right">{w.turnover}</TableCell>
                <TableCell className="text-right text-emerald-700 font-semibold">
                  ₹{w.revenue.toLocaleString("en-IN")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 5 · Billing & Collections                                         */
/* -------------------------------------------------------------------------- */

function BillingCollections({ data }) {
  const b = data.billing;
  return (
    <section data-testid="ipd-billing-collections">
      <SectionHeader title="Billing &amp; Collections" subtitle="Pending, overdue, and insurance claims" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard testid="ipd-bill-pending" icon={Wallet} label="Pending bills"
                    value={b.pending_bills} tone="amber" />
        <MetricCard testid="ipd-bill-overdue" icon={AlertTriangle} label="Overdue"
                    value={b.overdue_bills} tone="rose" />
        <MetricCard testid="ipd-bill-collection-rate" icon={Percent} label="Collection rate"
                    value={`${b.collection_rate_pct}%`} tone="emerald" />
        <MetricCard testid="ipd-bill-avg" icon={CreditCard} label="Avg bill"
                    value={`₹${b.avg_bill_inr.toLocaleString("en-IN")}`} tone="sky" />
        <Card className="p-5" data-testid="ipd-bill-insurance">
          <div className="flex items-center justify-between">
            <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Insurance</p>
            <div className="p-1.5 rounded-md bg-violet-50 text-violet-700"><ShieldCheck className="w-4 h-4" /></div>
          </div>
          <div className="mt-3 space-y-1 text-sm">
            <p><span className="text-slate-500">Pending:</span> <span className="font-semibold">{b.insurance_pending}</span></p>
            <p><span className="text-slate-500">Approved:</span> <span className="font-semibold text-emerald-700">{b.insurance_approved}</span></p>
            <p><span className="text-slate-500">Rejected:</span> <span className="font-semibold text-rose-700">{b.insurance_rejected}</span></p>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 6 · Inventory Alerts                                              */
/* -------------------------------------------------------------------------- */

function InventoryAlerts({ data }) {
  const inv = data.inventory;
  return (
    <section data-testid="ipd-inventory-alerts">
      <SectionHeader title="Inventory Alerts" subtitle="Low stock, expiring soon, and consumption" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4" data-testid="ipd-inv-low-stock">
          <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Low stock
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Reorder at</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.low_stock.map((r) => (
                <TableRow key={r.item}>
                  <TableCell>{r.item}</TableCell>
                  <TableCell className="text-right text-rose-700 font-semibold">{r.qty}</TableCell>
                  <TableCell className="text-right text-slate-500">{r.reorder_at}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="p-4" data-testid="ipd-inv-expiring">
          <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Package className="w-4 h-4 text-violet-600" /> Expiring soon
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead className="text-right">Expiry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.expiring_soon.map((r) => (
                <TableRow key={r.batch}>
                  <TableCell>{r.item}</TableCell>
                  <TableCell className="font-mono text-xs">{r.batch}</TableCell>
                  <TableCell className="text-right">{r.expiry}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="p-4" data-testid="ipd-inv-consumption">
          <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-sky-600" /> Top consumed (30d)
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Used</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.top_consumed.map((r) => (
                <TableRow key={r.item}>
                  <TableCell>{r.item}</TableCell>
                  <TableCell className="text-right font-semibold">{r.used}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 7 · OPD vs IPD Department Comparison                              */
/* -------------------------------------------------------------------------- */

function DepartmentCompare({ data }) {
  return (
    <section data-testid="ipd-dept-compare">
      <SectionHeader title="OPD vs IPD" subtitle="Cross-department revenue, patient volume and growth" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.deptCompare.map((d) => (
          <Card key={d.dept} className="p-5" data-testid={`ipd-dept-${d.dept.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="font-heading text-lg font-semibold">{d.dept}</p>
              <Badge className={d.growth_pct >= 10 ? "bg-emerald-500 hover:bg-emerald-500 text-white" : ""}
                     variant={d.growth_pct >= 10 ? "default" : "outline"}>
                +{d.growth_pct}%
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <p className="text-xs uppercase text-slate-500">Revenue</p>
                <p className="font-heading text-2xl font-bold text-emerald-700">
                  ₹{d.revenue.toLocaleString("en-IN")}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Patients</p>
                <p className="font-heading text-2xl font-bold text-sky-700">
                  {d.patients.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 8 · Staff Performance                                             */
/* -------------------------------------------------------------------------- */

function StaffPerformance({ data }) {
  return (
    <section data-testid="ipd-staff-performance">
      <SectionHeader title="Staff Performance" subtitle="Doctor utilization and nurse-to-patient ratio" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2" data-testid="ipd-doctor-utilization">
          <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-sky-600" /> Doctor utilization
          </p>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={data.staff.doctor_utilization} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} fontSize={11} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" fontSize={11} stroke="#94a3b8" width={120} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="utilization_pct" fill="#10b981" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 flex flex-col justify-center" data-testid="ipd-nurse-ratio">
          <div className="flex items-center gap-2 mb-2">
            <UserRound className="w-4 h-4 text-violet-600" />
            <p className="text-sm font-medium text-slate-700">Nurse : Patient</p>
          </div>
          <p className="font-heading text-5xl font-bold text-slate-900">{data.staff.nurse_ratio}</p>
          <p className="text-sm text-slate-500 mt-2">Below hospital target of 1 : 8 — healthy staffing.</p>
        </Card>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Root                                                                      */
/* -------------------------------------------------------------------------- */

export default function AdminIPDAnalytics() {
  const [filters, setFilters] = useState({
    from: "", to: "", ward: "all", doctor: "all", payment: "all",
  });
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data: payload } = await api.get("/ipd/analytics/summary");
        if (mounted) setData(toViewModel(payload));
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || "Failed to load analytics");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const wards = useMemo(() => data.wardTable.map((w) => w.name), [data]);
  const doctors = useMemo(() => data.topDoctors.map((d) => d.name), [data]);
  const hasAnyData =
    data.keyMetrics.total_beds > 0 ||
    data.revenueLast30d.length > 0 ||
    data.wardTable.length > 0;

  return (
    <div className="space-y-8" data-testid="admin-ipd-analytics">
      <FiltersBar filters={filters} setFilters={setFilters} doctors={doctors} wards={wards} />

      {loading && (
        <Card className="p-8 text-center text-slate-500" data-testid="ipd-analytics-loading">
          Loading IPD analytics…
        </Card>
      )}
      {!loading && error && (
        <Card className="p-8 text-center text-rose-600" data-testid="ipd-analytics-error">
          {error}
        </Card>
      )}
      {!loading && !error && !hasAnyData && (
        <Card className="p-8 text-center space-y-2" data-testid="ipd-analytics-empty">
          <p className="font-heading text-lg font-semibold text-slate-900">
            No IPD activity yet
          </p>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Add a ward and admit your first patient in the <strong>Receptionist → Manage IPD</strong> page
            to see occupancy, revenue and clinical insights here.
          </p>
        </Card>
      )}

      {!loading && !error && hasAnyData && (
        <>
          <KeyMetrics data={data} />
          <RevenueDashboard data={data} />
          <PatientAnalytics data={data} />
          <WardManagement data={data} />
          <BillingCollections data={data} />
          <InventoryAlerts data={data} />
          <DepartmentCompare data={data} />
          <StaffPerformance data={data} />
        </>
      )}
    </div>
  );
}
